/**
 * React state machine over the shared voice bridge (SD-211).
 *
 * idle -> connecting -> live -> idle. The bridge owns transport and audio;
 * this hook only mirrors its callbacks into render state for CallPanel.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { startVoiceCall, getVoiceApiBase, ApiError } from '../api/client';
import { createVoiceCall, type VoiceCallController } from './bridge';

export type VoiceStatus = 'idle' | 'connecting' | 'live';

export interface VoiceCallState {
  status: VoiceStatus;
  /** The fixed agent bubble's current text (§6.2: stays up between turns). */
  agentText: string;
  candidates: string[];
  muted: boolean;
  endingSoonSeconds: number | null;
  /**
   * Why the call ended abnormally, shown once then cleared. 'dropped' = a live
   * call lost its socket or engine; 'mic_denied'; otherwise a start failure
   * code (API error_code or 'voice_connect_failed').
   */
  endedError: string | null;
}

/** Give up on a socket that never reaches session_start (mobile dead zones). */
const CONNECT_TIMEOUT_MS = 10_000;

const INITIAL: VoiceCallState = {
  status: 'idle',
  agentText: '',
  candidates: [],
  muted: false,
  endingSoonSeconds: null,
  endedError: null,
};

/** Map the bridge's end reason to what the visitor should be told, if anything. */
function endedErrorFor(reason: string, wentLive: boolean): string | null {
  switch (reason) {
    case 'mic_denied':
      return 'mic_denied';
    // The socket closed on us, or the server cut a client that stopped reading.
    case 'disconnected':
    case 'slow_client':
      return 'dropped';
    // The engine failed: mid-call it is a drop, before session_start a start failure.
    case 'error':
      return wentLive ? 'dropped' : 'voice_connect_failed';
    // end / max_duration / idle / engine_closed / cancelled / disposed: expected.
    default:
      return null;
  }
}

export function useVoiceCall(params: { deliveryId: string; visitorId: string }) {
  const [state, setState] = useState<VoiceCallState>(INITIAL);
  const controllerRef = useRef<VoiceCallController | null>(null);
  const connectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether session_start was reached: an 'error' before it is a start
  // failure, after it a dropped call — the two need different wording.
  const wentLiveRef = useRef(false);
  // The bubble accumulates fragments; kept in a ref so each fragment does not
  // depend on the previous render having flushed.
  const agentTextRef = useRef('');
  // The meter updates as often as the waveform animates, which is no reason to
  // re-render the panel. CallPanel registers a painter here and the bridge's
  // own rAF loop drives it, so the level never enters render state and there
  // is no second loop (the widget consumer does the same thing).
  const levelSinkRef = useRef<((level: number) => void) | null>(null);

  const patch = useCallback((p: Partial<VoiceCallState>) => {
    setState((cur) => ({ ...cur, ...p }));
  }, []);

  const clearConnectTimer = useCallback(() => {
    if (connectTimerRef.current !== null) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
  }, []);

  const startCall = useCallback(async () => {
    if (controllerRef.current) return;
    agentTextRef.current = '';
    wentLiveRef.current = false;
    levelSinkRef.current?.(0);
    setState({ ...INITIAL, status: 'connecting' });

    const controller = createVoiceCall({
      onSessionStart: () => {
        clearConnectTimer();
        wentLiveRef.current = true;
        patch({ status: 'live' });
      },
      onAgentText: (text, reset) => {
        agentTextRef.current = reset ? text : agentTextRef.current + text;
        patch({ agentText: agentTextRef.current });
      },
      // The user spoke instead of tapping: the shown suggestions are spent.
      onUserTranscript: () => patch({ candidates: [] }),
      onCandidates: (_turnId, candidates) => patch({ candidates }),
      onInterrupted: () => patch({ candidates: [] }),
      onLevel: (level) => {
        levelSinkRef.current?.(level);
      },
      onEndingSoon: (seconds) => patch({ endingSoonSeconds: seconds }),
      onEnded: (reason) => {
        clearConnectTimer();
        controllerRef.current = null;
        levelSinkRef.current?.(0);
        setState({ ...INITIAL, endedError: endedErrorFor(reason, wentLiveRef.current) });
      },
    });
    controllerRef.current = controller;
    // Still synchronous with the click that called startCall: the contexts
    // must exist before the first await below or Safari/iOS keep them suspended.
    controller.prime();
    connectTimerRef.current = setTimeout(() => {
      connectTimerRef.current = null;
      if (controller.isActive()) return;
      // dispose() reports 'disposed' (no message); a timeout is a failure.
      controller.dispose();
      patch({ endedError: 'voice_connect_failed' });
    }, CONNECT_TIMEOUT_MS);

    try {
      const resp = await startVoiceCall(params);
      await controller.start({
        token: resp.session_token,
        wsUrl: resp.ws_url,
        apiBaseUrl: getVoiceApiBase(),
      });
    } catch (err) {
      clearConnectTimer();
      controller.dispose();
      controllerRef.current = null;
      const code = err instanceof ApiError ? err.errorCode : 'voice_connect_failed';
      setState({ ...INITIAL, endedError: code });
    }
  }, [params.deliveryId, params.visitorId, patch, clearConnectTimer]);

  const endCall = useCallback(() => {
    controllerRef.current?.end();
  }, []);

  const toggleMute = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller) return;
    controller.setMuted(!controller.isMuted());
    patch({ muted: controller.isMuted() });
  }, [patch]);

  const sendChip = useCallback(
    (text: string) => {
      controllerRef.current?.sendText(text);
      patch({ candidates: [] });
    },
    [patch],
  );

  const dismissError = useCallback(() => patch({ endedError: null }), [patch]);

  // The page going away is the end of the call (replica invariant: a dropped
  // WS finalizes server-side).
  useEffect(() => {
    return () => {
      if (connectTimerRef.current !== null) clearTimeout(connectTimerRef.current);
      controllerRef.current?.dispose();
    };
  }, []);

  return { state, levelSinkRef, startCall, endCall, toggleMute, sendChip, dismissError };
}
